// Számla létrehozása (full-page, draft-CRUD). A form-INSTANCE itt él (a 422 → mező-hiba a mutációból írható).
// A refs (picker) a választott ügyfélre particionál: ügyfél-váltáskor a client-scope fiók-lista a NEW ügyfél
// minden AKTÍV fiókjára áll (spec-default). A kliens ÖSSZEGET NEM küld — a body csak a bemeneti mezők; a
// totálokat + a tétel-összegeket a szerver számolja. Siker (201) → a szerkesztő-route-ra (edit) navigál.
// Dupla-submit ellen: busy-state (az internal busz az Idempotency-Key-t elutasítja). Page-gate: invoices.manage.
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { Button } from '@/components/ui/button';
import { invoiceKeys } from '@/features/invoices/api';
import { InvoiceForm } from '@/features/invoices/components/invoice-form';
import {
  addDaysIso,
  buildInvoiceErrorMap,
  emptyInvoiceForm,
  invoiceFormSchema,
  invoiceFormToCreateBody,
  todayIsoDate,
  type InvoiceFormValues,
} from '@/features/invoices/form';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

export const Route = createFileRoute('/invoices/new')({
  beforeLoad: () => requireAuth(),
  component: InvoiceCreatePage,
});

function InvoiceCreatePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const canManage = can('invoices.manage');

  const today = todayIsoDate();
  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema, {
      errorMap: buildInvoiceErrorMap(t),
    }) as Resolver<InvoiceFormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: emptyInvoiceForm(today, addDaysIso(today, 8)),
  });

  const clientId = form.watch('clientId');
  const refsKey = invoiceKeys.refs(clientId || '__none__');
  const refsQ = api.invoiceRefs.useQuery(
    refsKey,
    { query: clientId ? { clientId } : {} },
    { queryKey: refsKey },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  // Ügyfél-váltás: a client-scope fiók-lista a NEW ügyfél minden AKTÍV fiókjára áll (egyszer / ügyfél).
  const appliedClientRef = useRef<string>('');
  const isFetchingRefs = refsQ.isFetching;
  const { setValue } = form;
  useEffect(() => {
    if (!clientId || isFetchingRefs || !refs) return;
    if (appliedClientRef.current === clientId) return;
    appliedClientRef.current = clientId;
    const activeIds = refs.accounts.filter((a) => a.status === 'active').map((a) => a.id);
    setValue('accountIds', activeIds.length > 0 ? activeIds : refs.accounts.map((a) => a.id));
    setValue('accountId', '');
  }, [clientId, refs, isFetchingRefs, setValue]);

  const createMutation = useEntityMutation<InvoiceFormValues, unknown>({
    mutationFn: (values) => api.invoiceCreate.mutation({ body: invoiceFormToCreateBody(values) }),
    invalidateKeys: [invoiceKeys.lists()],
    onFieldErrors: (field, messages) =>
      form.setError(field as keyof InvoiceFormValues, { message: messages.join(', ') }),
    messages: {
      success: t('toast:invoices.created'),
      conflict: t('toast:invoices.conflict'),
      forbidden: t('toast:invoices.forbidden'),
      validation: t('toast:invoices.validation'),
      error: t('toast:invoices.error'),
    },
  });

  function onSubmit(values: InvoiceFormValues): void {
    createMutation.mutate(values, {
      onSuccess: (data) => {
        const parsed = z.object({ id: z.string() }).safeParse((data as { body: unknown }).body);
        if (parsed.success) {
          void navigate({ to: '/invoices/$id/edit', params: { id: parsed.data.id } });
        } else {
          void navigate({ to: '/invoices' });
        }
      },
    });
  }

  if (permsLoading) {
    return (
      <AppShell>
        <FormPage>
          <div className="text-muted-foreground text-sm">{t('invoices.loading')}</div>
        </FormPage>
      </AppShell>
    );
  }

  if (!canManage) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={t('invoices.forbidden')}
            description={t('invoices.forbiddenDesc')}
            action={
              <Button variant="outline" onClick={() => void navigate({ to: '/invoices' })}>
                {t('invoices.form.back')}
              </Button>
            }
          />
        </FormPage>
      </AppShell>
    );
  }

  // A cím a TOPBAR morzsájában él („Számlák / Új számla") — az oldal törzsében nincs h1, mert duplikálná.
  return (
    <AppShell
      parent={{ label: t('nav.invoices'), to: '/invoices' }}
      title={t('invoices.createTitle')}
      subtitle={t('invoices.createSubtitle')}
    >
      <FormPage>
        <InvoiceForm
          form={form}
          refs={refs}
          mode="create"
          pending={createMutation.isPending}
          onSubmit={onSubmit}
          t={t}
        />
      </FormPage>
    </AppShell>
  );
}
